import React, {useEffect, useState} from 'react';
import {Upload} from 'lucide-react';
import useFileUpload from '../hooks/useFileUpload';

const UploadAndProcessing = ({
                                 buttonTextUpload,
                                 buttonTextProcessing,
                                 apiUrl,
                                 exportName,
                                 onUploadSuccess,
                                 parameters = {
                                     qualificationParameter: 1.0,
                                     distanceParameter: 1.0,
                                     overtimeParameter: 1.0,
                                     languagesParameter: 1.0
                                 }
                             }) => {
    const [dragActive, setDragActive] = useState(false);
    const {
        isUploadSuccessfull,
        selectedFile,
        isProcessing,
        error,
        handleFileSelect,
        handleUploadAndProcess
    } = useFileUpload();


    useEffect(() => {
        // Notify the parent that the upload was successful
        if (isUploadSuccessfull) {
            onUploadSuccess();
        }
    }, [isUploadSuccessfull, onUploadSuccess]);


    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            const syntheticEvent = {
                target: {
                    files: [file]
                }
            };
            handleFileSelect(syntheticEvent);
        }
    };


    const handleUpload = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('file', selectedFile);

        await handleUploadAndProcess(apiUrl, exportName, parameters);
    };

    return (
        <main className="w-full max-w-md flex flex-col items-center justify-center mt-8 space-y-8">
            {/* Upload Card */}
            <label
                className={`w-64 h-64 shadow-lg rounded-2xl flex flex-col items-center justify-center p-6 cursor-pointer transition-colors relative
                ${dragActive ? 'bg-gray-700' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{
                    position: 'relative',
                    background: dragActive
                        ? 'linear-gradient(90deg, #3b82f6 25%, #60a5fa 50%, #3b82f6 75%)'
                        : 'linear-gradient(90deg, #64748b 25%, #94a3b8 50%, #64748b 75%)',
                    backgroundSize: '400% 100%',
                    animation: 'shimmer 2s infinite',
                    overflow: 'hidden',
                }}
            >
                <input
                    type="file"
                    className="hidden"
                    accept=".xlsx"
                    onChange={handleFileSelect}
                    disabled={isProcessing}
                />
                <div>
                    <Upload size={60} className="mb-4 text-white"/>
                </div>
                <h2 className="text-center font-semibold text-base text-white">{buttonTextUpload}</h2>

                {dragActive && (
                    <div
                        className="absolute inset-0 rounded-2xl border-2 border-blue-500 border-dashed flex items-center justify-center">
                        <p className="text-white font-semibold text-base">Drop file here</p>
                    </div>
                )}

                <style>{`
                @keyframes shimmer {
                    0% {
                        background-position: 0% 50%;
                    }
                    100% {
                        background-position: 100% 50%;
                    }
                }
            `}</style>
            </label>


            {/* Small Upload Button Below Drag-and-Drop Field */}
            <button
                className={`px-4 py-2 text-white rounded-md transition-colors border-2 ${
                    isProcessing || !selectedFile
                        ? 'border-gray-600 bg-transparent cursor-not-allowed'
                        : ''
                } ${isProcessing ? 'opacity-50' : ''}`}
                onClick={handleUpload}
                disabled={isProcessing || !selectedFile}
                style={
                    isProcessing || !selectedFile
                        ? {}
                        : {
                            background: 'linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)',
                            backgroundSize: '200% 200%',
                            animation: 'gradient 3s infinite',
                            borderColor: 'transparent',
                            color: 'white',
                        }
                }
            >
                {buttonTextProcessing}
            </button>

            <style>
                {`
                    @keyframes gradient {
                        0% {
                            background-position: 0% 50%;
                        }
                        50% {
                            background-position: 100% 50%;
                        }
                        100% {
                            background-position: 0% 50%;
                        }
                    }
                `}
            </style>


            {/* Loading Spinner */}
            {isProcessing && (
                <div className="flex items-center justify-center mt-4">
                    <svg className="animate-spin h-6 w-6 text-black" xmlns="http://www.w3.org/2000/svg" fill="none"
                         viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div
                    className="w-full text-sm text-red-400 p-3 bg-red-900 bg-opacity-50 rounded-2xl border border-red-500 mt-4 max-w-3xl whitespace-pre-wrap">
                    <strong>Error:</strong>
                    {"\n"}
                    {error.replace(/Blatt:/g, "\nBlatt:")}
                </div>
            )}
        </main>
    );
};

export default UploadAndProcessing;